import moment from 'moment';

import i18n from 'i18n';

import {
  GlobalNavigation_ProcessesSuccessFragment,
  ProcessStageStatus,
  ProcessState,
} from 'Src/generated/graphql';

import { EstimationHeuristic } from '../useEstimatedTimeForSteppedProcess';

type Process = Pick<
  GlobalNavigation_ProcessesSuccessFragment['nodes'][number],
  'createdAt' | 'state' | 'stages'
>;

type Params = {
  process: Process;
  options: {
    estimationHeuristic: EstimationHeuristic;
    maxExpectedMinutesPerStep: number;
    minExpectedMinutesPerStep: number;
    now: number;
  };
};

export function getEstimatedTimeForSteppedProcess(
  process: Process | null,
  {
    estimationHeuristic = EstimationHeuristic.AllStepsEqual,
    maxExpectedMinutesPerStep = 1.5,
    minExpectedMinutesPerStep = 1,
    now = moment.now(), // dependency injection for testing
  } = {},
): string {
  if (!process) {
    return i18n.t('Est._Time:_--', {
      strongStart: '',
      strongEnd: '',
    });
  }

  const isProcessFinished = process.state !== ProcessState.Processing;
  const params = {
    process,
    options: {
      estimationHeuristic,
      maxExpectedMinutesPerStep,
      minExpectedMinutesPerStep,
      now,
    },
  };

  return isProcessFinished
    ? getDateCompletedDisplayValueFromProcess(params)
    : getEstimatedTimeDisplayValueFromProcess(params);
}

function getEstimatedTimeDisplayValueFromProcess(params: Params): string {
  const { min, max } = getEstimatedMinMaxFromRunningProcess(params);

  // TODO: we don't want this to ever happen, it means there was a backend bug likely.
  if (min > 75) return 'Est. Time: 75+ min';
  return getDisplayValue(min, max);
}

function getDateCompletedDisplayValueFromProcess(params: Params): string {
  const isInSeconds = params.process.createdAt < 1e12;
  const normalizedCreatedAt = isInSeconds
    ? params.process.createdAt * 1000
    : params.process.createdAt;
  return moment(normalizedCreatedAt).format('l');
}

function getElapsedTimeFromProcessInSeconds(params: Params): number {
  const isInSeconds = params.process.createdAt < 1e12;
  const normalizedCreatedAt = isInSeconds
    ? params.process.createdAt * 1000
    : params.process.createdAt;
  return (params.options.now - normalizedCreatedAt) / 1000;
}

function getEstimatedMinMaxFromRunningProcess(
  params: Params,
): { max: number; min: number } {
  const elapsedTimeInSeconds = getElapsedTimeFromProcessInSeconds(params);
  const numCompletedStages = getNumberOfCompletedStagesFromProcess(params);

  const estimatedSecondsPerStage =
    elapsedTimeInSeconds / (numCompletedStages + 1);
  const isHalfwayDone = numCompletedStages >= params.process.stages.length / 2;
  const shouldUseOnlyObservedData =
    isHalfwayDone &&
    params.options.estimationHeuristic === EstimationHeuristic.AllStepsEqual;

  const expectedMaxMinutes =
    params.process.stages.length * params.options.maxExpectedMinutesPerStep;
  const expectedMinMinutes =
    params.process.stages.length * params.options.minExpectedMinutesPerStep;

  // with observed elapsed time data we can make an educated guess for this process.
  const observedMaxMinutes =
    (estimatedSecondsPerStage * params.process.stages.length) / 60;
  const observedMinMinutes = observedMaxMinutes * 0.75;

  // if we are not halfway done, lets use the expected guesses if they are higher. otherwise we get
  // weirdly low numbers in the first couple of stages.
  const maxMinutesToShowUser = Math.ceil(
    shouldUseOnlyObservedData
      ? observedMaxMinutes
      : Math.max(observedMaxMinutes, expectedMaxMinutes),
  );
  const minMinutesToShowUser = Math.round(
    shouldUseOnlyObservedData
      ? observedMinMinutes
      : Math.max(observedMinMinutes, expectedMinMinutes),
  );

  return {
    min: minMinutesToShowUser,
    max: maxMinutesToShowUser,
  };
}

function getDisplayValue(min: number, max: number): string {
  if (min === max) {
    return i18n.t('Est._Time:_%strongStart%min_min%strongEnd', {
      min,
      strongStart: '<strong>',
      strongEnd: '</strong>',
    });
  }

  return i18n.t('Est._Time:_%strongStart%min_-_%max_min%strongEnd', {
    min,
    max,
    strongStart: '<strong>',
    strongEnd: '</strong>',
  });
}

function getNumberOfCompletedStagesFromProcess(params: Params): number {
  const completedStages = params.process.stages.filter(
    (stage) => stage.status === ProcessStageStatus.Success,
  );
  return completedStages.length;
}
